import { defineCopy } from '@/copy';

/**
 * games strings. Voice: short, funny, second person, rib the player and never the person.
 * No em dashes. Placeholders use {name} and go through `fill()`.
 */
export const copy = defineCopy('games', {
  tiles: {
    '1v1': '1 on 1, ref or honor mode',
    '2v2': 'Two on two',
    '3v3': 'Three on three',
    '21': 'To 21, twos and threes',
    list: 'Every game on the books',
    listTitle: 'Games',
  },

  modes: {
    '1v1': '1v1',
    '2v2': '2v2',
    '3v3': '3v3',
    '4v4': '4v4',
    '5v5': '5v5',
  },

  newGame: {
    title: 'New game',
    line: 'Pick the squads. No take-backs.',
    format: 'Format',
    oneOnOneNote: 'One player a side. This counts as a 1v1.',
    who: {
      title: 'Who is playing',
      hint: 'Tap names. Teams sort themselves.',
      hereTag: 'Here',
      count: '{n} picked, {need} on court',
      sitters: '{n} on the bench',
      guestPlaceholder: 'Cousin with no phone',
      guestAdd: 'Add guest',
      needMore: 'Need {n} more',
      none: 'Nobody here yet. Add the crew in Crew first.',
    },
    teams: {
      title: 'Teams',
      a: 'Team Peach',
      b: 'Team Sky',
      balance: 'Balance',
      shuffle: 'Shuffle',
      swapHint: 'Tap a name to send them to the other side.',
      edge: '{pct} percent to win, on paper',
      even: 'Dead even on paper',
      bench: 'Bench',
      benchSide: 'Bench for {team}',
      sit: 'Sitting out',
      unrated: 'Rookies count as average.',
    },
    rules: {
      title: 'Rules',
      edit: 'Change',
      preset: { quick11: 'To 11', to21: 'To 21', iso3: 'To 3' },
      sheetTitle: 'House rules',
      scoring: 'Scoring',
      onesTwos: '1s and 2s',
      twosThrees: '2s and 3s',
      target: 'Play to',
      targetSuffix: 'points',
      winBy: 'Win by',
      winBy1: '1',
      winBy2: '2',
      cap: 'Hard cap',
      capOff: 'No cap',
      capOn: 'Cap at {n}',
      ft: 'Free throws',
      ftNone: 'None',
      ftBonus: 'Bonus',
      ftAndOne: 'And one',
      done: 'Done',
      line: 'To {target} / {scoring} / win by {winBy}{cap}',
      lineTimed: '{periods} x {minutes} min / {scoring}',
      lineCap: ' / cap {n}',
      scoringOnesTwos: '1s and 2s',
      scoringTwosThrees: '2s and 3s',
    },
    track: {
      title: 'What do we track',
      scoreOnly: 'Scores only',
      scoreOnlyHelp: 'Fast. Buckets and free throws.',
      misses: 'Misses',
      missesHelp: 'Bricks count too. Shooting percentages show up.',
      assists: 'Assists',
      assistsHelp: 'Quick prompt after each bucket. Who dished it?',
      stats: 'Full stat chips',
      statsHelp: 'Rebounds, steals, blocks, turnovers, fouls. Slow, and spicy.',
      honor: 'Honor mode',
      honorHelp: 'Two giant buttons. Players score themselves.',
      lineup: 'Subs',
      lineupHelp: 'Track who is on court when someone swaps in.',
      soloNote: 'One on one: nobody to pass to, so no assists or stat chips. Buckets and misses only.',
    },
    keeper: {
      title: 'Who keeps the book',
      me: 'Me',
      other: '{name} keeps it',
      help: 'The sitter keeps score. One tap and the book is theirs.',
      offlineNote:
        'No signal, so you keep the book. Hand them the phone, or give it to them in More once you are back online.',
      keptHere: 'No signal. You keep the book. Hand the phone to {name}, or give it over in More once you have signal.',
    },
    run: {
      partOf: 'Part of {name}',
      detach: 'Keep it separate',
      attach: 'Attach to the run',
    },
    start: 'Tip off',
    starting: 'Starting',
    startHint: 'Pick {n} more',
    failed: "Couldn't start that game. Check the roster and try again.",
    offline: 'No signal. The game starts on this phone and syncs later.',
    lastRules: 'Same rules as last time',
  },

  pad: {
    title: 'Keeper pad',
    back: 'Back',
    sun: 'Sun mode',
    more: 'More',
    youKeep: 'You have the book',
    someoneKeeps: '{name} has the book',
    nobodyKeeps: 'Nobody has the book',
    takeIt: 'Take it',
    takeTitle: 'Take the book?',
    takeBody: '{name} has the book. Taking it puts you on the scoring.',
    takeConfirm: 'Take it',
    tookIt: 'You have the book.',
    lostIt: '{name} took the book. Your phone is a spectator now.',
    inside: 'Inside',
    three: 'Three',
    ft: 'Free throw',
    miss: 'Miss',
    missOn: 'Miss on',
    undo: 'Undo',
    undoNothing: 'Nothing to undo',
    undone: 'Bucket removed. The ref has spoken.',
    undoneOther: 'That one is out of the book.',
    scoredFor: '{name} +{n}',
    scoredTeam: 'Team {team} +{n}, nobody credited. Tap who scored next time.',
    tapWho: 'Tap who scored first',
    tapTeam: 'Tap a team to give it points without a name.',
    selected: '{name} selected',
    selectedTeam: 'Team {team} selected',
    missed: '{name} missed a {what}',
    gamePoint: 'Game point',
    gamePointLine: 'GAME POINT. Somebody guard somebody.',
    fire: 'On fire',
    teamLabel: 'Team {team}',
    teamName: { A: 'Peach', B: 'Sky' },
    shortWhat: { inside: 'shot', three: 'three', ft: 'free throw' },
    assistTitle: 'Who dished it?',
    assistSkip: 'Nobody',
    assistDone: 'Assist to {name}.',
    benchTitle: 'Bench',
    benchHint: 'Tap a bench player, then who they replace.',
    subDone: 'Sub: {in} in for {out}.',
    subWrongSide: 'Subs stay on their own team.',
    chips: { reb: 'REB', stl: 'STL', blk: 'BLK', tov: 'TO', pf: 'FOUL' },
    chipDone: '{name}: {what}',
    chipNeedPlayer: 'Tap a player first.',
    blockNeedMiss: 'Log the miss first, then the block.',
    waiting: '{n} plays waiting for signal',
    waitingOne: '1 play waiting for signal',
    synced: 'All plays are in.',
    rejected: "These plays didn't count, someone else had the book.",
    rejectedCount: '{n} set aside.',
    lockedLate: 'The game was already in the books. Those plays missed the buzzer.',
    log: 'Play by play',
    logEmpty: 'No plays yet. The ball is still in the bag.',
    logLine: '{name} {what}',
    what: {
      three: 'hit a three',
      inside: 'scored inside',
      ft: 'made a free throw',
      missInside: 'missed inside',
      missThree: 'missed a three',
      missFt: 'missed a free throw',
      reb: 'grabbed a rebound',
      stl: 'stole it',
      blk: 'blocked a shot',
      tov: 'turned it over',
      pf: 'fouled',
      sub: 'came in for {out}',
      adjust: 'Team {team} {sign}{n}',
      end: 'Game over',
    },
    overflow: {
      title: 'Game controls',
      handoff: 'Hand the book to someone',
      handoffPick: 'Who gets the book?',
      handoffDone: 'The book is with {name}.',
      handoffFail: 'Needs signal. You keep the book for now.',
      rules: 'Change the rules',
      board: 'Open the courtside board',
      end: 'End the game',
      endHold: 'Hold to end',
      endBody: 'It counts as it stands. {a} to {b}.',
      abandon: 'Call it off, no result',
      abandonHold: 'Hold to abandon',
      leave: 'Back to the game page',
    },
    final: {
      title: 'Final?',
      line: '{a} to {b} for {name}.',
      lineTie: '{a} all.',
      yes: "Yes, it's over",
      undo: 'Undo last bucket',
      auto: 'Locks in {n}s',
      done: 'In the books.',
    },
    spectator: 'You are watching. {name} keeps the book.',
    wakeOff: 'Screen may sleep',
  },

  board: {
    title: 'Court-side board',
    turn: 'Turn me sideways',
    turnLine: 'This board likes landscape. Prop the phone on a bag.',
    live: 'Live',
    final: 'Final',
    paused: 'Reconnecting',
    keeper: 'Keeping: {name}',
    lastScore: '{name} +{n}',
    sitting: 'Sitting: {names}',
    wake: 'Screen stays on',
    wakeOff: 'Keep your screen on: tap here every few minutes',
    wakeOk: 'Screen stays awake',
    gamePoint: 'Game point',
    exit: 'Back',
    waiting: 'Waiting for tip off',
    gone: 'That game is gone. Somebody swept it away.',
  },

  game: {
    title: 'Game',
    notFound: 'No such game. Maybe it never tipped off.',
    notFoundBody: 'It may be on its way from a phone with no signal.',
    live: 'Live',
    finalTag: 'Final',
    winTag: 'Winner',
    abandonedTag: 'Called off',
    keepScore: 'Keep score',
    takeBook: 'Take the book',
    watch: 'Courtside board',
    keeping: 'Keeping: {name}',
    youKeep: 'You have the book',
    winner: '{name} win',
    winnerA: 'Team Peach wins',
    winnerB: 'Team Sky wins',
    tie: 'All square',
    abandoned: 'No result. Everybody wins, nobody counts.',
    potg: 'Player of the Game',
    potgScore: 'Game score {n}',
    worm: 'How it went',
    wormA: 'Peach ahead',
    wormB: 'Sky ahead',
    box: 'Box score',
    cols: {
      pts: 'PTS',
      fg: 'FG',
      tp: '3P',
      ft: 'FT',
      reb: 'REB',
      ast: 'AST',
      stl: 'STL',
      blk: 'BLK',
      tov: 'TO',
      pf: 'PF',
    },
    total: 'Total',
    unattributed: 'Team points, no name',
    notTracked: 'Not tracked in this game.',
    quickNote: 'Scores only game. Shooting numbers need misses tracked.',
    ratings: 'Rating moves',
    ratingsWait: 'Ratings update when you get signal.',
    ratingsLater: 'Ratings are on their way.',
    ratingUnchanged: 'No change',
    share: 'Share the result',
    shareBuilding: 'Making the card',
    shareDownloaded: 'Saved. Go brag.',
    shareFail: 'The card would not draw. Take a screenshot like it is 2015.',
    nextGame: 'Next game',
    fix: 'Fix the box score',
    fixLocked: 'Only the keeper (for a day) or the owner can fix this.',
    syncing: 'Plays still on their way: {n}',
    playedAt: 'Played {when}',
    run: 'Part of {name}',
    deleted: 'Game removed.',
    deleteOk: 'Game removed. It can come back for 30 days.',
    backToList: 'All games',
    backToGauntlet: 'Back to the Gauntlet',
    backToBracket: 'Back to the bracket',
  },

  fix: {
    title: 'Fix the box score',
    line: 'Void a bucket, or give a team points nobody owns. Every fix is on the record.',
    voidBtn: 'Void',
    voided: 'Voided',
    empty: 'Nothing to fix. Clean sheet.',
    adjustTitle: 'Team points',
    adjustA: 'Team Peach',
    adjustB: 'Team Sky',
    adjustPlus: '+1',
    adjustMinus: '-1',
    adjustDone: 'Team {team} {sign}1. Noted.',
    rulesTitle: 'Wrong rules?',
    rulesLine: 'We played {other}. Switch it and the whole game re-scores.',
    rulesApply: 'Switch to {name}',
    removeTitle: 'Remove the game',
    removeLine: 'Owner only. It leaves every list and stat, and can return for 30 days.',
    removeHold: 'Hold to remove',
    close: 'Done',
    failed: 'That fix needs signal. Try again in a bit.',
  },

  list: {
    title: 'Games',
    line: 'Every game on the books, newest first.',
    mine: 'Mine',
    all: 'All',
    empty: 'No games yet. The court is lonely.',
    liveNow: 'Live now',
    more: 'Older games',
    loading: 'Pulling the tape',
    row: '{mode} to {target}',
    rowTimed: '{mode} timed',
    keeper: 'Keeping: {name}',
  },

  live: {
    title: 'Live now',
    line: 'Nobody is hooping right now. Suspicious.',
    resume: 'Game in progress',
    resumeLine: 'You have the book. Pick it back up.',
    open: 'Open',
    board: 'Board',
    keep: 'Keep',
    keeping: 'Keeping: {name}',
    waiting: '{n} plays waiting for signal',
  },

  next: {
    title: 'Next game',
    line: 'Same rules, fresh legs.',
    plan: 'Next up',
    sits: '{name} sits and keeps the book',
    sitsMany: '{names} sit out',
    stays: 'Winners stay',
    rotate: 'Rotate everyone',
    ruleLabel: 'Who sits',
    tipOff: 'Tip off',
    change: 'Change',
    again: 'Run it back',
    noPlan: 'Pick the squads for the next one.',
    teamLine: '{a} vs {b}',
    starting: 'Starting',
    book: 'Book goes to {name}',
    noKeeper: 'Nobody sits, you keep the book.',
    offlineNote: 'No signal, so you keep the book. Hand {name} the phone, or give it over in More once you are online.',
    pickRule: 'Rotation',
  },

  share: {
    title: 'TTS Basketball',
    final: 'FINAL',
    potg: 'Player of the Game',
    footer: 'ttsbasketball',
    teamA: 'PEACH',
    teamB: 'SKY',
    fileName: 'tts-game-{score}.png',
    stats: '{pts} pts',
  },

  errors: {
    generic: 'Something slipped. Try again.',
    offline: 'No signal right now.',
    loadFail: "Couldn't load that game.",
    somebody: 'Somebody',
  },
});
